import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useOidc } from '@nohara-dxdev/react-oidc';

import { PropertiesDetailForm } from '../components/properties/properties-detail-form';
import { propertiesService } from '../services/properties.service';

import { paths } from '@/config/paths';
import { useConfirmDialog } from '@/hooks/use-confirm-dialog';
import { useLoadingProgress } from '@/hooks/use-loading-progress';
import { useSnackbar } from '@/hooks/use-snackbar';
import { MESSAGES } from '@/lib/constants';
import { useSubmitting } from '@/hooks/use-submitting';
import { checkPermission } from '@/utils/helpers';
import { FunctionName, PermissionAction } from '@/lib/enums';

export const PropertiesDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { propertyId } = useParams<{ propertyId: string }>();

  const { setConfirmDialog } = useConfirmDialog();
  const { setIsLoading } = useLoadingProgress();
  const { setSnackbarMessage } = useSnackbar();
  const { setIsSubmitting } = useSubmitting();
  const { permissions } = useOidc();
  const [canUpdateProperty, setCanUpdateProperty] = useState<boolean>(false);
  useEffect(() => {
    setCanUpdateProperty(
      checkPermission(permissions, FunctionName.PROPERTY_MANAGEMENT, PermissionAction.UPDATE)
    );
  }, [permissions]);
  const handleCancel = () => {
    navigate(paths.properties.path);
  };

  const deleteProperty = async (propertyId: string) => {
    try {
      setIsLoading(true);
      await propertiesService.deleteProperty(propertyId);
      // Navigate back to properties list after successful deletion
      setIsSubmitting(true);
      navigate(paths.properties.path);
      setSnackbarMessage(MESSAGES.DELETE_PROPERTY);
      setIsLoading(false);
    } catch (error) {
      console.error('Error deleting property:', error);
      const message = '削除に失敗しました。もう一度お試しください。';
      setIsLoading(false);
      setConfirmDialog({
        title: message,
      });
    }
  };

  const handleDelete = async () => {
    if (!propertyId) {
      return;
    }

    setConfirmDialog({
      title: '現場情報を削除します。現場に紐付く受領資料はすべて削除され、復旧できません。',
      confirmDialog: () => {
        void deleteProperty(propertyId);
      },
      confirmButtonText: '削除',
      cancelButtonText: 'キャンセル',
    });
  };

  const handleArchive = async (isArchive: boolean) => {
    try {
      if (!propertyId) {
        return;
      }
      setIsLoading(true);
      if (isArchive === false) {
        await propertiesService.archiveProperty([propertyId]);
        setSnackbarMessage(MESSAGES.ARCHIVED);
      } else {
        await propertiesService.restoreProperty([propertyId]);
        setSnackbarMessage(MESSAGES.UN_ARCHIVED);
      }
      setIsSubmitting(true);
      navigate(paths.properties.path);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error('Error archiving property:', error);
      setConfirmDialog({
        title: 'アーカイブに失敗しました。もう一度お試しください',
      });
    }
  };

  return (
    <PropertiesDetailForm
      onCancel={handleCancel}
      onDelete={handleDelete}
      onArchive={handleArchive}
      canUpdateProperty={canUpdateProperty}
    />
  );
};
